import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { of, forkJoin, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpParams } from '@angular/common/http';
import { ButtonsTranslations } from '~shared/components/buttons/buttons-translations.service';
import { FormLabelTranslationService } from '~shared/components/form-fields/translations/form-label-translations.service';
import { PaginatedTableComponent } from '~shared/components/tables/paginated-table/paginated-table.component';
import { TooltipTranslationService } from '~shared/translations';
import { SpinnerService } from '~shared/components/buttons/spinner/spinner.service';
import { EventKpis, ExecutionIteration, Lottery, SidebarOptions } from '~core/models';
import { AttributeService, NavigationService, ScreenSizeService, TixxSelectService } from '~core/services';
import { SidebarService } from '~core/components/sidebar/sidebar.service';
import {ExecutionIterationStatusType} from '~core/enum';

/**
 * Component that displays the lottery dashboard
 */
@UntilDestroy()
@Component({
  selector: 'app-lottery-dashboard',
  templateUrl: './lottery-dashboard.component.html'
})

export class LotteryDashboardComponent implements OnInit, OnDestroy {
  @ViewChild(PaginatedTableComponent) paginatedTable: PaginatedTableComponent;

  public selectedLottery: Lottery;
  public eventKpis: EventKpis;
  public executionIterations: Array<ExecutionIteration>;
  public isOpened: boolean = true;
  public loading: boolean = true;
  public columns: Array<string> = [];
  public keys: Array<Array<string>> = [];

  public interval: any;
  public listOfPanels: any = {
    primaryCategories: true,
    lotteryRounds: true,
  };

  public spinnerName = 'eventDashboardRefresh';

  constructor(
    private navigationService: NavigationService,
    private sidebarService: SidebarService,
    private tixxSelectService: TixxSelectService,
    protected attributeService: AttributeService,
    public buttonsTranslations: ButtonsTranslations,
    public flTranslations: FormLabelTranslationService,
    public tooltipTranslations: TooltipTranslationService,
    private spinnerService: SpinnerService,
    private screenSizeService: ScreenSizeService,
  ) {
    if (this.screenSizeService.screenSize === 'desktop') {
      this.sidebarService.setSidebar(new SidebarOptions(true, true));
    } else {
      this.sidebarService.setSidebar(new SidebarOptions(false, true));
    }
    this.sidebarService.castIsOpened.pipe(untilDestroyed(this))
      .subscribe(sidebarOptions => this.isOpened = sidebarOptions.isOpened);
    this.navigationService.setCurrentPage('lotteryDashboard');
    this.navigationService.setCurrentBreadcrumbs(['crm', 'lotteryDashboard']);
    this.tixxSelectService.castSelectedLottery.pipe(untilDestroyed(this))
      .subscribe((lottery: Lottery) => this.selectedLottery = lottery);
  }

  ngOnInit(): void {
    this.setUp();
  }

  ngOnDestroy(): void {
    clearInterval(this.interval);
  }

  /**
   * Calls the event KPIs
   */
  protected setUp(): void {
    const calls: Array<undefined | Observable<any>> = [
      this.tixxSelectService.getEventKpis(this.selectedLottery.id).pipe(untilDestroyed(this), catchError(error => of(null)))
    ];
    if (this.selectedLottery.executionId) {
      const params = new HttpParams().append('status[]', ExecutionIterationStatusType.EXECUTION).append('status[]', ExecutionIterationStatusType.CREATED);
      calls.push(this.tixxSelectService.getExecutionIterations(this.selectedLottery.executionId, params)
        .pipe(untilDestroyed(this), catchError(error => of(null))));
    }

    forkJoin(calls).subscribe(([eventKpis, executionIterations]) => {
      this.eventKpis = eventKpis as EventKpis;
      if (executionIterations) {
        this.executionIterations = executionIterations as Array<ExecutionIteration>;
      }
      this.loading = false;
    });

    this.interval = setInterval(() => {
      this.spinnerService.show(this.spinnerName);
      forkJoin(calls).subscribe(([eventKpis, executionIterations]) => {
        this.eventKpis = eventKpis as EventKpis;
        if (executionIterations) {
          this.executionIterations = executionIterations as Array<ExecutionIteration>;
        }
        this.spinnerService.hide(this.spinnerName);
      });
    }, 10000);
  }

  public openLottery() {
    window.open(this.selectedLottery.website.lotteryEventUrl);
  }
}
